import React, { useState, useEffect } from 'react';
import './createTorneoModal.css';

const CreateTorneoModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    nome: '',
    sottotitolo: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  // Reset form quando il modal si apre
  useEffect(() => {
    if (isOpen) {
      setFormData({ nome: '', sottotitolo: '' });
      setErrors({});
      setIsSubmitting(false);
      setIsClosing(false);
    }
  }, [isOpen]);

  // Gestione ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen && !isSubmitting) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
      // Previeni scroll del body quando il modal è aperto
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isSubmitting]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Rimuovi errore quando l'utente inizia a digitare
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.nome.trim()) {
      newErrors.nome = 'Il nome del torneo è obbligatorio';
    } else if (formData.nome.trim().length < 3) {
      newErrors.nome = 'Il nome deve contenere almeno 3 caratteri';
    } else if (formData.nome.trim().length > 100) {
      newErrors.nome = 'Il nome non può superare i 100 caratteri';
    }
    
    if (!formData.sottotitolo.trim()) {
      newErrors.sottotitolo = 'Il sottotitolo è obbligatorio';
    } else if (formData.sottotitolo.trim().length < 3) {
      newErrors.sottotitolo = 'Il sottotitolo deve contenere almeno 3 caratteri';
    } else if (formData.sottotitolo.trim().length > 200) {
      newErrors.sottotitolo = 'Il sottotitolo non può superare i 200 caratteri';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simula una chiamata API
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Chiama la funzione di callback con i dati del form
      const torneoData = {
        nome: formData.nome.trim(),
        sottotitolo: formData.sottotitolo.trim(),
        edizioni: 0,
        status: 'draft',
        edizioniDate: []
      };
      
      onSubmit(torneoData);
      handleClose();
    } catch (error) {
      console.error('Errore durante la creazione:', error);
      setErrors({ submit: 'Errore durante la creazione del torneo. Riprova.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) return;
    
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`modal-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="modal-container">
        {/* Header */}
        <div className="torneo-modal-header">
          <h2 className="modal-title">
            <i className="fas fa-plus-circle"></i>
            Nuovo Torneo
          </h2>
          <button 
            className="modal-close-btn"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Chiudi modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Corpo */}
        <div className="modal-body">
          <p className="modal-description">
            Crea un nuovo torneo inserendo le informazioni di base. Potrai aggiungere 
            altre informazioni e configurazioni dopo la creazione iniziale.
          </p>

          <form onSubmit={handleSubmit} className="modal-form">
            {/* Nome torneo */}
            <div className={`form-group ${errors.nome ? 'error' : ''}`}>
              <label htmlFor="nome">
                <i className="fas fa-trophy"></i>
                Nome Torneo <span className="required">*</span>
              </label>
              <input
                type="text"
                id="nome"
                value={formData.nome}
                onChange={(e) => handleInputChange('nome', e.target.value)}
                placeholder="Es. Torneo Nazionale Scoutball 2024"
                disabled={isSubmitting}
                maxLength={100}
              />
              {errors.nome && (
                <span className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  {errors.nome}
                </span>
              )}
            </div>

            {/* Sottotitolo */}
            <div className={`form-group ${errors.sottotitolo ? 'error' : ''}`}>
              <label htmlFor="sottotitolo">
                <i className="fas fa-align-left"></i>
                Sottotitolo <span className="required">*</span>
              </label>
              <textarea
                id="sottotitolo"
                value={formData.sottotitolo}
                onChange={(e) => handleInputChange('sottotitolo', e.target.value)}
                placeholder="Es. La competizione più grande d'Italia per scout di tutte le età"
                disabled={isSubmitting}
                maxLength={200}
                rows={3}
              />
              {errors.sottotitolo && (
                <span className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  {errors.sottotitolo}
                </span>
              )}
            </div>

            {/* Errore di submit */}
            {errors.submit && (
              <div className="error-message" style={{ marginTop: '1rem' }}>
                <i className="fas fa-exclamation-triangle"></i>
                {errors.submit}
              </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            type="button"
            className="modal-btn modal-btn-cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <i className="fas fa-times"></i>
            Annulla
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.nome.trim() || !formData.sottotitolo.trim()}
          >
            {isSubmitting ? (
              <>
                <div className="modal-loading-spinner"></div>
                Creazione...
              </>
            ) : (
              <>
                <i className="fas fa-plus"></i>
                Crea Torneo
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTorneoModal;